import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { EVENEMENT_SESSION_EXPIREE, ErreurApi, appelApi, urlApi } from './api';
import { TOUS_LES_SITES } from '../site/context';
import type { TypeFacturation } from './useFinancements';

/**
 * Factures et avoirs émis par la plateforme (TF-407, sans Tiime), leurs
 * règlements reçus (TF-408) et les factures calculées des échéances (TF-410)
 * et des frais de 1er équipement (TF-411).
 *
 * Ils se tiennent dans le bloc Financement du dossier, par contrat, et se
 * suivent ensemble dans l'écran « Factures » (TF-422), où leur envoi au client
 * se note à la main. État,
 * totaux, reste dû et ce qui empêche chaque geste — émettre, corriger,
 * supprimer, établir un avoir, saisir ou retirer un règlement — viennent de
 * l'API (`domain/facturation.ts`, `domain/emission-facture.ts`) : l'écran les
 * affiche sans les recalculer.
 */

export type UniteFacturation = 'MOIS' | 'JOUR' | 'HEURE' | 'FORFAIT';

export type ModeReglement = 'VIREMENT' | 'CHEQUE' | 'PRELEVEMENT' | 'CARTE' | 'ESPECES' | 'AUTRE';

/** Un règlement reçu ; retiré, il reste lisible et ne compte plus. */
export interface Reglement {
  id: string;
  /** `AAAA-MM-JJ`. */
  date: string;
  montant: string;
  mode: ModeReglement;
  modeLibelle: string;
  reference: string | null;
  commentaire: string | null;
  saisiLe: string;
  saisiPar: string | null;
  retrait: { le: string; par: string | null; motif: string } | null;
  refusRetrait: string | null;
}

export interface LigneFacture {
  libelle: string;
  quantite: string;
  unite: UniteFacturation;
  uniteLibelle: string;
  prixUnitaire: string | null;
  montant: string;
  tauxTva: string;
}

export interface Facture {
  id: string;
  nature: 'FACTURE' | 'AVOIR';
  natureLibelle: string;
  objet: string;
  objetLibelle: string;
  statut: 'BROUILLON' | 'EMISE';
  numero: string | null;
  periode: { debut: string; fin: string; texte: string };
  client: string | null;
  emiseLe: string | null;
  emisePar: string | null;
  dateEcheancePaiement: string | null;
  totaux: { ht: string; tva: string; ttc: string; ttcCentimes: number };
  situation: { etat: string; libelle: string; ton: string; detail: string | null; enRetard: boolean };
  avoirable: { centimes: number; texte: string };
  /** Réglé et reste dû d'une facture émise ; `null` pour un brouillon ou un avoir. */
  solde: { regle: string; resteDu: string; resteDuCentimes: number } | null;
  reglements: Reglement[];
  lignes: LigneFacture[];
  motif: string | null;
  origine: { id: string; numero: string | null } | null;
  avoirs: { id: string; numero: string | null }[];
  fichier: boolean;
  /**
   * Facture calculée — échéance (TF-410), 1er équipement (TF-411) : ce que le
   * calcul proposait ; `null` pour une facture saisie.
   */
  calcul: CalculFacture | null;
  /**
   * Facture d'une échéance à partir de la deuxième : son certificat de
   * réalisation, établi (`id`) ou à établir (TF-412) ; `null` sinon.
   */
  certificat: { id: string | null; texte: string } | null;
  refus: {
    /** Corriger ligne par ligne : une facture saisie. */
    modification: string | null;
    /** Recalculer une facture calculée. */
    recalcul: string | null;
    /** En corriger le montant : jamais celui d'une facture aux heures (TF-416). */
    correction: string | null;
    suppression: string | null;
    emission: string | null;
    avoir: string | null;
    reglement: string | null;
  };
}

/** Le calcul d'une facture calculée. */
export interface CalculFacture {
  /** « Calcul cumulé » pour une échéance, « Calcul » pour le 1er équipement. */
  titre: string;
  /** « 7 392,00 € × 6 mois / 12 mois = 3 696,00 €, moins 1 848,00 € déjà facturés », « 500,00 € pris en charge » */
  detail: string;
  /** Montant proposé, « 1 848,00 € ». */
  propose: string;
  /** Le montant a été corrigé à la main. */
  corrige: boolean;
  calculeLe: string;
  /** Ce qui a changé depuis le calcul ; `null` : à jour. */
  aRevoir: string | null;
}

export interface SaisieLigneFacture {
  libelle: string;
  quantite: string;
  unite: UniteFacturation;
  prixUnitaire: string;
  tauxTva: string;
}

export interface SaisieReglement {
  dateReglement: string;
  montant: string;
  mode: ModeReglement;
  reference: string;
  commentaire: string;
}

export interface SaisieFacture {
  periodeDebut: string;
  periodeFin: string;
  lignes: SaisieLigneFacture[];
}

/** L'original émis, servi par une route gardée et tracée. */
export function urlOriginalFacture(id: string): string {
  return urlApi(`/factures/${id}/fichier`);
}

/** Un brouillon tel qu'il sortirait, sans numéro. */
export function urlApercuFacture(id: string): string {
  return urlApi(`/factures/${id}/apercu.pdf`);
}

/** Toute pièce touchée change le bloc Financement de son dossier. */
function useInvalidation() {
  const client = useQueryClient();
  return () => client.invalidateQueries({ queryKey: ['financements'] });
}

export function usePreparerFacture() {
  const invalider = useInvalidation();
  return useMutation({
    mutationFn: ({ contratId, saisie }: { contratId: string; saisie: SaisieFacture }) =>
      appelApi<{ facture: Facture }>('/factures', { methode: 'POST', corps: { contratId, ...saisie } }),
    onSuccess: invalider,
  });
}

/** Prépare la facture calculée d'une échéance (TF-410). */
export function usePreparerFactureEcheance() {
  const invalider = useInvalidation();
  return useMutation({
    mutationFn: (echeanceId: string) =>
      appelApi<{ facture: Facture }>(`/factures/echeances/${echeanceId}`, { methode: 'POST' }),
    // Un refus (échéance préparée entre-temps sur un autre poste) rafraîchit aussi le bloc.
    onSettled: invalider,
  });
}

/** Prépare la facture à part des frais de 1er équipement d'un contrat (TF-411). */
export function usePreparerFacturePremierEquipement() {
  const invalider = useInvalidation();
  return useMutation({
    mutationFn: (contratId: string) =>
      appelApi<{ facture: Facture }>(`/factures/premier-equipement/${contratId}`, { methode: 'POST' }),
    // Un refus (facture préparée entre-temps sur un autre poste) rafraîchit aussi le bloc.
    onSettled: invalider,
  });
}

/** Prépare la facture des heures d'un mois sous l'accord d'un dossier OF (TF-416). */
export function usePreparerFactureHeuresMensuelles() {
  const invalider = useInvalidation();
  return useMutation({
    mutationFn: ({ accordId, mois }: { accordId: string; mois: string }) =>
      appelApi<{ facture: Facture }>(`/factures/heures-mensuelles/${accordId}`, { methode: 'POST', corps: { mois } }),
    // Un refus (mois préparé entre-temps sur un autre poste) rafraîchit aussi le bloc.
    onSettled: invalider,
  });
}

/** Prépare la facture unique de fin de parcours sous l'accord France Travail d'un dossier OF (TF-418). */
export function usePreparerFactureFinParcours() {
  const invalider = useInvalidation();
  return useMutation({
    mutationFn: (accordId: string) =>
      appelApi<{ facture: Facture }>(`/factures/fin-parcours/${accordId}`, { methode: 'POST' }),
    // Un refus (facture préparée entre-temps sur un autre poste) rafraîchit aussi le bloc.
    onSettled: invalider,
  });
}

/** Refait le calcul d'une facture calculée sur les données du jour. */
export function useRecalculerFacture() {
  const invalider = useInvalidation();
  return useMutation({
    mutationFn: (id: string) => appelApi<{ facture: Facture }>(`/factures/${id}/recalcul`, { methode: 'POST' }),
    onSettled: invalider,
  });
}

/** Corrige le libellé et le montant d'une facture calculée. */
export function useCorrigerMontantFacture() {
  const invalider = useInvalidation();
  return useMutation({
    mutationFn: ({ id, libelle, montant }: { id: string; libelle: string; montant: string }) =>
      appelApi<{ facture: Facture }>(`/factures/${id}/montant`, { methode: 'PUT', corps: { libelle, montant } }),
    onSuccess: invalider,
  });
}

export function useModifierFacture() {
  const invalider = useInvalidation();
  return useMutation({
    mutationFn: ({ id, saisie }: { id: string; saisie: SaisieFacture }) =>
      appelApi<{ facture: Facture }>(`/factures/${id}`, { methode: 'PUT', corps: saisie }),
    onSuccess: invalider,
  });
}

export function useSupprimerFacture() {
  const invalider = useInvalidation();
  return useMutation({
    mutationFn: (id: string) => appelApi<void>(`/factures/${id}`, { methode: 'DELETE' }),
    onSuccess: invalider,
  });
}

export function useEmettreFacture() {
  const invalider = useInvalidation();
  return useMutation({
    mutationFn: (id: string) => appelApi<{ facture: Facture }>(`/factures/${id}/emission`, { methode: 'POST' }),
    // Un refus (pièce émise entre-temps, fiche devenue incomplète) rafraîchit aussi le bloc.
    onSettled: invalider,
  });
}

export function useEtablirAvoir() {
  const invalider = useInvalidation();
  return useMutation({
    mutationFn: ({ id, motif, montant }: { id: string; motif: string; montant: string | null }) =>
      appelApi<{ avoir: Facture }>(`/factures/${id}/avoir`, { methode: 'POST', corps: { motif, montant } }),
    onSettled: invalider,
  });
}

export function useSaisirReglement() {
  const invalider = useInvalidation();
  return useMutation({
    mutationFn: ({ id, saisie }: { id: string; saisie: SaisieReglement }) =>
      appelApi<{ facture: Facture }>(`/factures/${id}/reglements`, { methode: 'POST', corps: saisie }),
    // Un refus (règlement saisi entre-temps par un autre poste) rafraîchit aussi le bloc.
    onSettled: invalider,
  });
}

export function useRetirerReglement() {
  const invalider = useInvalidation();
  return useMutation({
    mutationFn: ({ id, reglementId, motif }: { id: string; reglementId: string; motif: string }) =>
      appelApi<{ facture: Facture }>(`/factures/${id}/reglements/${reglementId}/retrait`, {
        methode: 'POST',
        corps: { motif },
      }),
    onSettled: invalider,
  });
}

// ---------------------------------------------------------------------------
// Liste et suivi des factures (TF-422)
// ---------------------------------------------------------------------------

export type TypeClient = 'OPCO' | 'FRANCE_TRAVAIL' | 'AUTRE' | 'ENTREPRISE';

export type EtatFacture =
  | 'A_EMETTRE'
  | 'EMISE'
  | 'PARTIELLEMENT_REGLEE'
  | 'EN_RETARD'
  | 'REGLEE'
  | 'ANNULEE'
  | 'AVOIR_EMIS';

export type EtatEnvoi = 'A_ENVOYER' | 'ENVOYEE' | 'SANS_ENVOI';

export type CanalEnvoi = 'PORTAIL_FINANCEUR' | 'CHORUS_PRO' | 'PLATEFORME_AGREEE' | 'EMAIL' | 'COURRIER' | 'AUTRE';

/** L'envoi d'une pièce émise à son client, noté à la main par le pôle. */
export interface EnvoiFacture {
  etat: EtatEnvoi;
  libelle: string;
  ton: string;
  /** `AAAA-MM-JJ` ; `null` tant qu'il n'est pas noté. */
  le: string | null;
  canal: CanalEnvoi | null;
  canalLibelle: string | null;
  reference: string | null;
  par: string | null;
  /** « Envoyée le … · canal · réf. … », ou pourquoi la pièce n'a pas à partir. */
  detail: string | null;
  /** Rejet par son client en attente de renvoi : la pièce est « à renvoyer » (TF-403). */
  rejet: { le: string; motif: string; par: string | null; texte: string } | null;
}

export interface LigneSuiviFacture {
  id: string;
  nature: 'FACTURE' | 'AVOIR';
  natureLibelle: string;
  objetLibelle: string;
  statut: 'BROUILLON' | 'EMISE';
  numero: string | null;
  apprenant: { id: string; nom: string; prenom: string };
  formation: string | null;
  siteCode: string;
  client: { cle: string | null; nom: string | null; type: TypeClient | null; typeLibelle: string | null };
  reference: string | null;
  /** Numéro de pro forma (TF-465) : figé à l'émission, celui du bloc sinon. */
  numeroProForma: string | null;
  /** Apprentissage, contrat de professionnalisation, France Travail (TF-465) ; `null` sans prise en charge. */
  typeFacturation: { valeur: TypeFacturation; libelle: string } | null;
  periode: { debut: string; fin: string; texte: string };
  creeLe: string;
  /** Jour d'émission, `AAAA-MM-JJ`. */
  emiseLe: string | null;
  dateEcheancePaiement: string | null;
  /** Total TTC ; celui d'un avoir est positif. */
  ttc: { texte: string; centimes: number };
  solde: { regle: string; resteDu: string; resteDuCentimes: number } | null;
  situation: { etat: EtatFacture; libelle: string; ton: string; detail: string | null; enRetard: boolean };
  envoi: EnvoiFacture | null;
  origine: { id: string; numero: string | null } | null;
  avoirs: { id: string; numero: string | null }[];
}

export interface ListeSuiviFactures {
  lignes: LigneSuiviFacture[];
  compteurs: {
    pieces: number;
    aEmettre: number;
    /** Pièces qui ont à partir, rejetées à renvoyer comprises. */
    aEnvoyer: number;
    aRenvoyer: number;
    nonReglees: { nombre: number; centimes: number };
    enRetard: { nombre: number; centimes: number };
    nonRegleesTexte: string;
    enRetardTexte: string;
  };
  clients: { cle: string; nom: string; type: TypeClient; typeLibelle: string }[];
  filtres: {
    typesClient: { valeur: TypeClient; libelle: string }[];
    typesFacturation: { valeur: TypeFacturation; libelle: string }[];
    etats: { valeur: EtatFacture; libelle: string }[];
    envois: { valeur: EtatEnvoi; libelle: string }[];
  };
  canaux: { valeur: CanalEnvoi; libelle: string }[];
  /** Les fichiers de l'export (TF-423), leurs colonnes et leurs formats. */
  export: ReferentielExportFactures;
}

// ---------------------------------------------------------------------------
// Export de la liste (TF-423)
// ---------------------------------------------------------------------------

export type JeuExportFactures = 'PIECES' | 'REGLEMENTS';
export type FormatExportFactures = 'XLSX' | 'CSV';

export interface ColonneExportFactures {
  cle: string;
  libelle: string;
  /** Ne se décoche pas : la ligne ne désignerait plus rien sans elle. */
  obligatoire: boolean;
}

export interface ReferentielExportFactures {
  jeux: { valeur: JeuExportFactures; libelle: string; description: string; colonnes: ColonneExportFactures[] }[];
  formats: { valeur: FormatExportFactures; libelle: string }[];
  /** Au-delà, l'export est refusé : la liste est à restreindre d'abord. */
  piecesMax: number;
}

export interface DemandeExportFactures {
  jeu: JeuExportFactures;
  format: FormatExportFactures;
  colonnes: string[];
  site: string;
  /** Les pièces émises de la liste filtrée, toutes pages comprises. */
  ids: string[];
}

/**
 * Télécharge l'export. Il passe par `fetch` plutôt que par un lien : la
 * demande porte la liste des pièces, et un refus de l'API (aucun règlement,
 * liste trop longue) doit s'afficher à l'écran, pas s'enregistrer comme un
 * faux fichier. Rend le nombre de lignes du fichier.
 */
export async function exporterFactures(demande: DemandeExportFactures): Promise<{ nomFichier: string; lignes: number }> {
  const reponse = await fetch(urlApi('/factures/export'), {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ...demande, site: demande.site === TOUS_LES_SITES ? undefined : demande.site }),
  });
  if (!reponse.ok) {
    const corps = (await reponse.json().catch(() => null)) as
      | { erreur?: { code?: string; message?: string; details?: unknown } }
      | null;
    if (reponse.status === 401) window.dispatchEvent(new Event(EVENEMENT_SESSION_EXPIREE));
    throw new ErreurApi(
      reponse.status,
      corps?.erreur?.code ?? 'ERREUR',
      corps?.erreur?.message ?? 'L’export n’a pas pu être produit.',
      corps?.erreur?.details,
    );
  }
  const nomFichier =
    /filename="([^"]+)"/.exec(reponse.headers.get('content-disposition') ?? '')?.[1] ??
    (demande.format === 'XLSX' ? 'factures.xlsx' : 'factures.csv');
  const url = URL.createObjectURL(await reponse.blob());
  const lien = document.createElement('a');
  lien.href = url;
  lien.download = nomFichier;
  document.body.appendChild(lien);
  lien.click();
  document.body.removeChild(lien);
  URL.revokeObjectURL(url);
  return { nomFichier, lignes: Number(reponse.headers.get('x-lignes') ?? 0) };
}

/** Ce qu'une pièce émise a figé, tel qu'imprimé sur l'original. */
export interface MentionsFigees {
  client: { nom: string | null; adresse: string | null; siret: string | null; typeLibelle: string | null };
  reference: string | null;
  numeroProForma: string | null;
  apprenant: string | null;
  formation: string | null;
  datesFormation: string | null;
  heures: string | null;
}

export interface FicheFacture {
  facture: Facture;
  apprenant: { id: string; nom: string; prenom: string; siteCode: string; formation: string | null };
  /** `null` pour un brouillon. */
  mentions: MentionsFigees | null;
  /** `null` pour un brouillon. */
  envoi: EnvoiFacture | null;
  /** Le fil de la pièce : ses gestes et ses notes, du plus récent au plus ancien. */
  suivi: EvenementSuivi[];
  refus: { envoi: string | null; retraitEnvoi: string | null; rejet: string | null; retraitRejet: string | null };
  referentiel: {
    modesReglement: { valeur: ModeReglement; libelle: string }[];
    canaux: { valeur: CanalEnvoi; libelle: string }[];
  };
}

export interface SaisieEnvoi {
  date: string;
  canal: CanalEnvoi;
  reference: string;
}

/** Un geste du journal sur la pièce, ou une note de suivi (TF-403). */
export interface EvenementSuivi {
  id: string;
  type: 'GESTE' | 'NOTE';
  /** Instant, ISO. */
  le: string;
  auteur: string | null;
  libelle: string;
  texte: string | null;
  changements: { champ: string; avant: string; apres: string }[];
  noteId: string | null;
  retrait: { le: string; par: string | null } | null;
  peutRetirer: boolean;
}

export interface SaisieRejet {
  date: string;
  motif: string;
}

/**
 * Toutes les factures et tous les avoirs des antennes du lecteur (écran
 * « Factures »). Sous la clé `financements` : un geste sur une pièce, ici ou
 * dans le bloc Financement, rafraîchit la liste.
 */
export function useListeFactures(site: string) {
  return useQuery({
    queryKey: ['financements', 'factures', site],
    queryFn: () =>
      appelApi<ListeSuiviFactures>(
        `/factures${site && site !== TOUS_LES_SITES ? `?site=${encodeURIComponent(site)}` : ''}`,
      ),
  });
}

/** La fiche d'une pièce : sa vue, ce qu'elle a figé, son envoi. */
export function useFicheFacture(id: string | null) {
  return useQuery({
    queryKey: ['financements', 'facture', id],
    queryFn: () => appelApi<FicheFacture>(`/factures/${id}`),
    enabled: id !== null,
  });
}

/** Note ou corrige l'envoi d'une pièce émise à son client. */
export function useNoterEnvoi() {
  const invalider = useInvalidation();
  return useMutation({
    mutationFn: ({ id, saisie }: { id: string; saisie: SaisieEnvoi }) =>
      appelApi<FicheFacture>(`/factures/${id}/envoi`, { methode: 'PUT', corps: saisie }),
    onSettled: invalider,
  });
}

/** Retire un envoi noté par erreur. */
export function useRetirerEnvoi() {
  const invalider = useInvalidation();
  return useMutation({
    mutationFn: (id: string) => appelApi<FicheFacture>(`/factures/${id}/envoi`, { methode: 'DELETE' }),
    onSettled: invalider,
  });
}

/** Note le rejet d'une pièce envoyée par son client : elle est à renvoyer. */
export function useNoterRejet() {
  const invalider = useInvalidation();
  return useMutation({
    mutationFn: ({ id, saisie }: { id: string; saisie: SaisieRejet }) =>
      appelApi<FicheFacture>(`/factures/${id}/rejet`, { methode: 'PUT', corps: saisie }),
    onSettled: invalider,
  });
}

/** Retire un rejet noté par erreur. */
export function useRetirerRejet() {
  const invalider = useInvalidation();
  return useMutation({
    mutationFn: (id: string) => appelApi<FicheFacture>(`/factures/${id}/rejet`, { methode: 'DELETE' }),
    onSettled: invalider,
  });
}

/** Ajoute une note de suivi à une pièce. */
export function useAjouterNoteSuivi() {
  const invalider = useInvalidation();
  return useMutation({
    mutationFn: ({ id, texte }: { id: string; texte: string }) =>
      appelApi<FicheFacture>(`/factures/${id}/notes`, { methode: 'POST', corps: { texte } }),
    onSettled: invalider,
  });
}

/** Son auteur retire une note de suivi : elle reste au fil, barrée. */
export function useRetirerNoteSuivi() {
  const invalider = useInvalidation();
  return useMutation({
    mutationFn: ({ id, noteId }: { id: string; noteId: string }) =>
      appelApi<FicheFacture>(`/factures/${id}/notes/${noteId}/retrait`, { methode: 'POST' }),
    onSettled: invalider,
  });
}
